import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Tooltip,
  Card,
  CardContent,
  Divider,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Autocomplete,
  InputAdornment,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Shuffle as ShuffleIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  LockReset as LockResetIcon,
  PersonOff as PersonOffIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { User, Permission, PermissionDto, PermissionCategoryDto } from '../../types';
import { apiService } from '../../services/api';
import { isValidEmail } from '../../utils/validation';
import { generateRandomPassword } from '../../utils/helpers';
import PageHeader from '../../components/common/PageHeader';
import DocumentManagement from './components/DocumentManagement';
import ShareManagement from './components/ShareManagement';
import EmailTemplateManagement from './components/EmailTemplateManagement';

interface UserFormData {
  userId: string;
  username: string;
  password: string;
  firstName: string;
  lastName: string;
  email: string;
  profilePicture: string;
  permissions: number[]; // Changed to permission IDs
  active: boolean;
}

const Admin: React.FC = () => {
  // Tab state
  const [currentTab, setCurrentTab] = useState(0);
  
  const [users, setUsers] = useState<User[]>([]);
  const [permissions, setPermissions] = useState<PermissionDto[]>([]);
  const [permissionCategories, setPermissionCategories] = useState<PermissionCategoryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter states
  const [permissionFilter, setPermissionFilter] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<string>('');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  
  // Password visibility states
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [resetPasswordResult, setResetPasswordResult] = useState<string>('');
  
  // Form state
  const [formData, setFormData] = useState<UserFormData>({
    userId: '',
    username: '',
    password: '',
    firstName: '',
    lastName: '',
    email: '',
    profilePicture: '',
    permissions: [],
    active: true,
  });
  
  // Validation state
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  
  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersData, permissionsData, categoriesData] = await Promise.all([
        apiService.getUsers(),
        apiService.getPermissions(),
        apiService.getPermissionsByCategory()
      ]);
      
      setUsers(usersData);
      setPermissions(permissionsData);
      setPermissionCategories(categoriesData);
      setError(null);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter users based on permission and search
  const filteredUsers = users.filter(user => {
    const matchesPermission = !permissionFilter || user.permissions.includes(permissionFilter as Permission);
    const matchesSearch = !searchFilter || 
      user.firstName.toLowerCase().includes(searchFilter.toLowerCase()) ||
      user.lastName.toLowerCase().includes(searchFilter.toLowerCase()) ||
      user.username.toLowerCase().includes(searchFilter.toLowerCase()) ||
      user.email.toLowerCase().includes(searchFilter.toLowerCase());
    
    return matchesPermission && matchesSearch;
  });

  // Reset form
  const resetForm = useCallback(() => {
    setFormData({
      userId: '',
      username: '',
      password: '',
      firstName: '',
      lastName: '',
      email: '',
      profilePicture: '',
      permissions: [],
      active: true,
    });
    setValidationErrors({});
  }, []);

  // Validate form
  const validateForm = useCallback((data: UserFormData, isEdit: boolean = false): boolean => {
    const errors: Record<string, string> = {};

    // Don't validate userId for create form since it will be auto-generated
    if (!data.username.trim()) errors.username = 'Username is required';
    if (!isEdit && !data.password.trim()) errors.password = 'Password is required';
    if (data.password && data.password.length < 8) errors.password = 'Password must be at least 8 characters';
    if (!data.firstName.trim()) errors.firstName = 'First name is required';
    if (!data.lastName.trim()) errors.lastName = 'Last name is required';
    if (!data.email.trim()) errors.email = 'Email is required';
    if (data.email && !isValidEmail(data.email)) errors.email = 'Invalid email format';
    if (data.permissions.length === 0) errors.permissions = 'At least one permission is required';

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, []);

  // Handle form field changes
  const handleFieldChange = useCallback((field: keyof UserFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Generate random password
  const handleGeneratePassword = useCallback(() => {
    const newPassword = generateRandomPassword(10);
    handleFieldChange('password', newPassword);
  }, [handleFieldChange]);

  // Show snackbar
  const showSnackbar = useCallback((message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  // Handle create user
  const handleCreateUser = useCallback(async () => {
    if (!validateForm(formData)) return;

    try {
      // Convert permission IDs back to permission names for the API
      const selectedPermissions = permissions
        .filter(p => formData.permissions.includes(p.permissionId))
        .map(p => p.permissionName);

      // Provide a temporary userId - the API should generate the actual one
      await apiService.createUser({
        userId: 'temp', // This will be replaced by the API
        username: formData.username,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        profilePicture: formData.profilePicture || null,
        permissions: selectedPermissions as Permission[],
        active: true, // New users are active by default
        firstLogin: true, // New users must change password on first login
      });

      showSnackbar('User created successfully');
      setCreateDialogOpen(false);
      resetForm();
      loadData();
    } catch (err) {
      console.error('Error creating user:', err);
      showSnackbar('Failed to create user', 'error');
    }
  }, [formData, validateForm, showSnackbar, resetForm, loadData, permissions]);

  // Handle edit user
  const handleEditUser = useCallback(async () => {
    if (!selectedUser || !validateForm(formData, true)) return;

    try {
      // Convert permission IDs back to permission names for the API
      const selectedPermissions = permissions
        .filter(p => formData.permissions.includes(p.permissionId))
        .map(p => p.permissionName);

      await apiService.updateUser(selectedUser.userId, {
        username: formData.username,
        password: formData.password || undefined,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        profilePicture: formData.profilePicture || undefined,
        permissions: selectedPermissions as Permission[],
        active: formData.active,
      });

      showSnackbar('User updated successfully');
      setEditDialogOpen(false);
      resetForm();
      setSelectedUser(null);
      loadData();
    } catch (err) {
      console.error('Error updating user:', err);
      showSnackbar('Failed to update user', 'error');
    }
  }, [selectedUser, formData, validateForm, showSnackbar, resetForm, loadData, permissions]);

  // Handle delete user
  const handleDeleteUser = useCallback(async () => {
    if (!selectedUser) return;

    try {
      await apiService.deleteUser(selectedUser.userId);
      showSnackbar('User deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      loadData();
    } catch (err) {
      console.error('Error deleting user:', err);
      showSnackbar('Failed to delete user', 'error');
    }
  }, [selectedUser, showSnackbar, loadData]);

  // Handle reset password
  const handleResetPassword = useCallback(async () => {
    if (!selectedUser) return;

    try {
      const result = await apiService.resetPassword(selectedUser.userId);
      setResetPasswordResult(result.tempPassword);
      showSnackbar('Password reset successfully');
      loadData(); // Reload to update FirstLogin status
    } catch (err) {
      console.error('Error resetting password:', err);
      showSnackbar('Failed to reset password', 'error');
    }
  }, [selectedUser, showSnackbar, loadData]);

  // Handle deactivate user
  const handleDeactivateUser = useCallback(async () => {
    if (!selectedUser) return;

    try {
      await apiService.updateUser(selectedUser.userId, {
        active: false
      });
      showSnackbar('User deactivated successfully');
      setDeactivateDialogOpen(false);
      setSelectedUser(null);
      loadData();
    } catch (err) {
      console.error('Error deactivating user:', err);
      showSnackbar('Failed to deactivate user', 'error');
    }
  }, [selectedUser, showSnackbar, loadData]);

  // Open reset password dialog
  const openResetPasswordDialog = useCallback((user: User) => {
    setSelectedUser(user);
    setResetPasswordResult('');
    setResetPasswordDialogOpen(true);
  }, []);

  // Open deactivate dialog
  const openDeactivateDialog = useCallback((user: User) => {
    setSelectedUser(user);
    setDeactivateDialogOpen(true);
  }, []);

  // Open create dialog
  const openCreateDialog = useCallback(() => {
    resetForm();
    setCreateDialogOpen(true);
  }, [resetForm]);

  // Open edit dialog
  const openEditDialog = useCallback((user: User) => {
    setSelectedUser(user);
    
    // Convert permission names to permission IDs
    const userPermissionIds = permissions
      .filter(p => user.permissions.includes(p.permissionName as Permission))
      .map(p => p.permissionId);

    setFormData({
      userId: user.userId,
      username: user.username,
      password: '',
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      profilePicture: user.profilePicture || '',
      permissions: userPermissionIds,
      active: user.active,
    });
    setEditDialogOpen(true);
  }, [permissions]);

  // Open delete dialog
  const openDeleteDialog = useCallback((user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  }, []);

  // Get user status
  const getUserStatus = useCallback((user: User) => {
    if (user.permissions.includes('admin')) return 'Admin';
    return 'User';
  }, []);

  // Get status color
  const getStatusColor = useCallback((user: User) => {
    if (user.permissions.includes('admin')) return 'error';
    return 'primary';
  }, []);

  // Clear filters
  const clearFilters = useCallback(() => {
    setPermissionFilter('');
    setSearchFilter('');
  }, []);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Administration"
        subtitle="Manage system users, permissions, and document templates"
      />

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={(_, newValue) => setCurrentTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="User Management" />
          <Tab label="Document Management" />
          <Tab label="Document Templates" />
          <Tab label="Email Templates" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {currentTab === 0 && (
        <>
          {/* Summary Cards */}
          <Stack direction="row" spacing={3} sx={{ mb: 4 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PersonIcon color="primary" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h4">{users.length}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Users
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <AdminIcon color="error" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h4">
                  {users.filter(u => u.permissions.includes('admin')).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Administrators
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Stack>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <FilterIcon color="action" />
          <Typography variant="h6">Filters</Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            startIcon={<ClearIcon />}
            onClick={clearFilters}
            disabled={!permissionFilter && !searchFilter}
          >
            Clear Filters
          </Button>
        </Stack>
        
        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Permission</InputLabel>
            <Select
              value={permissionFilter}
              onChange={(e) => setPermissionFilter(e.target.value)}
              label="Filter by Permission"
            >
              <MenuItem value="">All Permissions</MenuItem>
              {permissions.map((permission) => (
                <MenuItem key={permission.permissionId} value={permission.permissionName}>
                  {permission.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            label="Search Users"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            placeholder="Search by name, username, or email"
            sx={{ minWidth: 300 }}
          />
        </Stack>
      </Paper>

      {/* Users Table */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            System Users ({filteredUsers.length} of {users.length})
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
          >
            Add User
          </Button>
        </Box>
        <Divider />
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Permissions</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.userId}>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2">
                        {user.firstName} {user.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        @{user.username} • ID: {user.userId}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={getUserStatus(user)}
                      color={getStatusColor(user)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {user.permissions.slice(0, 3).map((permission) => {
                        const permissionData = permissions.find(p => p.permissionName === permission);
                        return (
                          <Chip
                            key={permission}
                            label={permissionData?.displayName || permission.replace('_', ' ')}
                            size="small"
                            variant="outlined"
                          />
                        );
                      })}
                      {user.permissions.length > 3 && (
                        <Chip
                          label={`+${user.permissions.length - 3} more`}
                          size="small"
                          variant="outlined"
                          color="secondary"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit User">
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(user)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reset Password">
                      <IconButton
                        size="small"
                        color="warning"
                        onClick={() => openResetPasswordDialog(user)}
                      >
                        <LockResetIcon />
                      </IconButton>
                    </Tooltip>
                    {user.active ? (
                      <Tooltip title="Deactivate User">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => openDeactivateDialog(user)}
                        >
                          <PersonOffIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="User Inactive">
                        <IconButton
                          size="small"
                          disabled
                        >
                          <PersonOffIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete User">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => openDeleteDialog(user)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
        </>
      )}

      {/* Document Management Tab */}
      {currentTab === 1 && (
        <DocumentManagement />
      )}
      
      {/* PDF Templates Tab */}
      {currentTab === 2 && (
        <ShareManagement />
      )}

      {/* Email Templates Tab */}
      {currentTab === 3 && (
        <EmailTemplateManagement />
      )}

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Username"
              value={formData.username}
              onChange={(e) => handleFieldChange('username', e.target.value)}
              error={!!validationErrors.username}
              helperText={validationErrors.username || "User ID will be auto-generated"}
            />
            
            <TextField
              fullWidth
              label="Password"
              type={showCreatePassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => handleFieldChange('password', e.target.value)}
              error={!!validationErrors.password}
              helperText={validationErrors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={showCreatePassword ? "Hide Password" : "Show Password"}>
                      <IconButton
                        onClick={() => setShowCreatePassword(!showCreatePassword)}
                        edge="end"
                      >
                        {showCreatePassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Generate Random Password">
                      <IconButton
                        onClick={handleGeneratePassword}
                        edge="end"
                      >
                        <ShuffleIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => handleFieldChange('firstName', e.target.value)}
                error={!!validationErrors.firstName}
                helperText={validationErrors.firstName}
              />
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => handleFieldChange('lastName', e.target.value)}
                error={!!validationErrors.lastName}
                helperText={validationErrors.lastName}
              />
            </Stack>
            
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              error={!!validationErrors.email}
              helperText={validationErrors.email}
            />
            
            <TextField
              fullWidth
              label="Profile Picture URL (Optional)"
              value={formData.profilePicture}
              onChange={(e) => handleFieldChange('profilePicture', e.target.value)}
            />
            
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Permissions {validationErrors.permissions && (
                  <Typography component="span" color="error" variant="caption">
                    - {validationErrors.permissions}
                  </Typography>
                )}
              </Typography>
              <Autocomplete
                multiple
                options={permissions}
                getOptionLabel={(option) => option.displayName}
                groupBy={(option) => option.category}
                value={permissions.filter(p => formData.permissions.includes(p.permissionId))}
                onChange={(_, newValue) => {
                  handleFieldChange('permissions', newValue.map(p => p.permissionId));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select permissions..."
                    error={!!validationErrors.permissions}
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    <Box>
                      <Typography variant="body2">{option.displayName}</Typography>
                      {option.description && (
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.displayName}
                      size="small"
                      {...getTagProps({ index })}
                    />
                  ))
                }
              />
            </Box>
            
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateUser} variant="contained">
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                label="User ID"
                value={formData.userId}
                disabled
                helperText="User ID cannot be changed"
              />
              <TextField
                fullWidth
                label="Username"
                value={formData.username}
                onChange={(e) => handleFieldChange('username', e.target.value)}
                error={!!validationErrors.username}
                helperText={validationErrors.username}
              />
            </Stack>
            
            
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => handleFieldChange('firstName', e.target.value)}
                error={!!validationErrors.firstName}
                helperText={validationErrors.firstName}
              />
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => handleFieldChange('lastName', e.target.value)}
                error={!!validationErrors.lastName}
                helperText={validationErrors.lastName}
              />
            </Stack>
            
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              error={!!validationErrors.email}
              helperText={validationErrors.email}
            />
            
            <TextField
              fullWidth
              label="Profile Picture URL (Optional)"
              value={formData.profilePicture}
              onChange={(e) => handleFieldChange('profilePicture', e.target.value)}
            />
            
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Permissions {validationErrors.permissions && (
                  <Typography component="span" color="error" variant="caption">
                    - {validationErrors.permissions}
                  </Typography>
                )}
              </Typography>
              <Autocomplete
                multiple
                options={permissions}
                getOptionLabel={(option) => option.displayName}
                groupBy={(option) => option.category}
                value={permissions.filter(p => formData.permissions.includes(p.permissionId))}
                onChange={(_, newValue) => {
                  handleFieldChange('permissions', newValue.map(p => p.permissionId));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select permissions..."
                    error={!!validationErrors.permissions}
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    <Box>
                      <Typography variant="body2">{option.displayName}</Typography>
                      {option.description && (
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.displayName}
                      size="small"
                      {...getTagProps({ index })}
                    />
                  ))
                }
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditUser} variant="contained">
            Update User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user "{selectedUser?.firstName} {selectedUser?.lastName}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onClose={() => setResetPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reset User Password</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Reset password for user "{selectedUser?.firstName} {selectedUser?.lastName}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            This will generate a new temporary password and require the user to change it on their next login.
          </Typography>
          
          {resetPasswordResult && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                New temporary password generated:
              </Typography>
              <Typography variant="h6" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {resetPasswordResult}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Please share this password with the user. They will be required to change it on their next login.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordDialogOpen(false)}>
            {resetPasswordResult ? 'Close' : 'Cancel'}
          </Button>
          {!resetPasswordResult && (
            <Button onClick={handleResetPassword} color="warning" variant="contained">
              Reset Password
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Deactivate User Dialog */}
      <Dialog open={deactivateDialogOpen} onClose={() => setDeactivateDialogOpen(false)}>
        <DialogTitle>Deactivate User</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to deactivate user "{selectedUser?.firstName} {selectedUser?.lastName}"?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The user will no longer be able to log in to the system. You can reactivate them later if needed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeactivateUser} color="error" variant="contained">
            Deactivate User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Admin;
